import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, firstValueFrom } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { RuntimeConfigService } from "../../../services/runtime-config.service";

/** Shape of the CMS raw-content response for ?properties=TourSchoolLink. */
interface SchoolTourRawContent {
  TourSchoolLink?: {
    Type: string;
    Value: string | null;
    FileUrl: string;
  };
}

/**
 * Looks up a school's tour-scheduling URL (usually a Calendly link) from the public
 * nhaschools.com CMS — the same per-school raw-content source the meals and care lookups
 * use, and the property behind the Scholar portal's "Schedule your tour" button. Managed by
 * NHA content editors; a school with no link set simply can't offer tour scheduling.
 */
@Injectable({
  providedIn: 'root'
})
export class SchoolTourService {
  // Tour links change at content-editor cadence — cache for hours, not seconds.
  private static readonly CACHE_MINUTES = 6 * 60;

  // One shared lookup per school per session; persisted lookups are served from hybrid storage.
  private tourUrlBySchoolEID = new Map<number, Observable<string | null>>();

  constructor(
    private http: HttpClient,
    private hybridStorageService: HybridStorageService,
    private runtimeConfig: RuntimeConfigService
  ) { }

  // Public, unauthenticated endpoint (CORS *), keyed by the NHA/SIS integer school ID
  // (School.SchoolEID / the portal's StudentInSchoolRecord.SchoolID — NOT this app's local
  // Core.Schools.Id).
  private get cmsRawContentUrl(): string {
    return `${this.runtimeConfig.currentConfig.cmsBaseUrl}/api/schools/raw-content`;
  }

  /**
   * Emits the school's tour-scheduling URL, or null when the school has none or the lookup
   * fails — callers disable/hide the action either way. Treat the URL as opaque and link to it.
   */
  getTourLink(schoolEID: number): Observable<string | null> {
    let tourUrl$ = this.tourUrlBySchoolEID.get(schoolEID);
    if (!tourUrl$) {
      tourUrl$ = from(this.loadTourLink(schoolEID)).pipe(shareReplay(1));
      this.tourUrlBySchoolEID.set(schoolEID, tourUrl$);
    }
    return tourUrl$;
  }

  private async loadTourLink(schoolEID: number): Promise<string | null> {
    const cacheKey = HybridStorageKeys.SchoolTour_LinkPrefix + schoolEID;

    // Stored as { url } so a cached "no tour link" (null) is distinguishable from a cache miss.
    const stored = await this.hybridStorageService.getItemIfFresh<{ url: string | null }>(
      cacheKey, SchoolTourService.CACHE_MINUTES);
    if (stored) {
      return stored.url;
    }

    try {
      // A school ID unknown to the CMS returns 204 No Content; HttpClient yields a null body.
      const response = await firstValueFrom(this.http.get<SchoolTourRawContent | null>(
        `${this.cmsRawContentUrl}/${schoolEID}?properties=TourSchoolLink`));
      const url = response?.TourSchoolLink?.Value?.trim() || null;

      // Cache empty results too — "no tour link" is a valid, stable answer for that school.
      await this.hybridStorageService.setItem(cacheKey, { url });
      return url;
    } catch (error) {
      // Fail soft: the button stays disabled. Not cached, and the in-session share is
      // dropped, so the next open retries.
      console.error('Error fetching tour link for school', schoolEID, error);
      this.tourUrlBySchoolEID.delete(schoolEID);
      return null;
    }
  }
}
